/**
 * Created by Ning Jiang on 2/28/2018.
 */

import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {ISymbolStateConfig, SymbolStateTransition} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Bounce, Timeline, TimelineLite, TweenLite, TweenMax} from "gsap";
import {WildNudgeGameConfig} from "../WildNudgeGameConfig";
import {WildNudgeSoundConfig} from "../WildNudgeSoundConfig";
import {WildNudgeReelSymbol} from "./WildNudgeReelSymbol";

export class WildNudgeScatterReelSymbol extends WildNudgeReelSymbol {

    private _attentionOverlayContainer:PIXI.Container;
    private _fireOverlay:TimelineSprite;
    private _fireLoopTimeline:Timeline;
    private _flickingOverlay:PIXI.Sprite;
    private _flickingTween:TweenMax;
    private _isAttentionPlaying:boolean = false;
    private _removeAttentionTimeline:TimelineLite;

    private _freespinOverlayContainer:PIXI.Container;
    private _fsFireOverlay:TimelineSprite;
    private _fsFireLoopTimeline:Timeline;
    private _isFsFirePlaying:boolean = false;
    private _removeFsAttentionTimeline:TimelineLite;

    constructor(symName:string, reelId:number, state?:ISymbolStateConfig) {
        super(symName, reelId, state);
    }

    protected initState(stateConfig:ISymbolStateConfig):boolean {
        if(super.initState(stateConfig)) {
            // To move the patches always on the top.
            if(this._attentionOverlayContainer) {
                this.addChild(this._attentionOverlayContainer);
            }

            if(this._freespinOverlayContainer) {
                this.addChild(this._freespinOverlayContainer);
            }

            return true;
        }

        return false;
    }

    protected addNewStateAnimation(timeline:TimelineLite, newState:SymbolStateTransition, stateAnimation:TimelineSprite, delay:number):void {
        super.addNewStateAnimation(timeline, newState, stateAnimation, delay);

        if(newState.state.keyword === WildNudgeGameConfig.instance.SYMBOL_STATES.attention.keyword || newState.state.keyword === WildNudgeGameConfig.instance.SYMBOL_STATES.win.keyword) {
            timeline.add(this.playScatterAttentionOverlay(), delay);
        }
    }

    protected addOldStateAnimation(timeline:TimelineLite, transition:SymbolStateTransition, stateAnimation:TimelineSprite, delay:number):void {
        super.addOldStateAnimation(timeline, transition, stateAnimation, delay);

        if(transition.state.keyword != WildNudgeGameConfig.instance.SYMBOL_STATES.attention.keyword && transition.state.keyword != WildNudgeGameConfig.instance.SYMBOL_STATES.win.keyword) {
            timeline.add(() => this.removeScatterAttentionOverlay(), delay);
        }
        if(transition.state.keyword != WildNudgeGameConfig.instance.SYMBOL_STATES.spin.keyword) {
            timeline.add(() => this.removeFreespinScatterAttentionOverlay(), delay);
        }
    }

    private playScatterAttentionOverlay():TimelineLite {
        if(this._isAttentionPlaying) {
            return new TimelineLite();
        }

        if(!this._attentionOverlayContainer) {
            this._flickingOverlay = new PIXI.Sprite(GameResources.getTextures("S_win")[0]);
            this._flickingOverlay.anchor.set(0.5, 0.5);

            this._fireOverlay = new TimelineSprite(GameResources.getTextures("S_fire"));
            this._fireOverlay.blendMode = PIXI.BLEND_MODES.ADD;
            this._fireOverlay.position.set(-GameConfig.instance.SYMBOL_WIDTH * 0.5 - 20, -GameConfig.instance.SYMBOL_HEIGHT * 0.5 - 9);
            this._fireOverlay.scale.set(.85);

            this._attentionOverlayContainer = new PIXI.Container();
            this._attentionOverlayContainer.addChild(this._flickingOverlay);
            this._attentionOverlayContainer.addChild(this._fireOverlay);
        }

        const timeline:TimelineLite = new TimelineLite();
        timeline.add(() => {
            this._fireLoopTimeline = this._fireOverlay.getAnimation([0, 16], undefined, () => this.loopAttentionFire());
            this._flickingTween = TweenMax.fromTo(this._flickingOverlay, 0.08, {alpha : 0}, {
                alpha : 1,
                repeat : 100,
                yoyo : true,
                ease : Bounce.easeOut
            });
            this.addChild(this._attentionOverlayContainer);
        });
        this._isAttentionPlaying = true;

        return timeline;
    }

    private loopAttentionFire():void {
        this._fireLoopTimeline = this._fireOverlay.getAnimation([17, 40], undefined, () => this.loopAttentionFire());
    }

    private removeScatterAttentionOverlay():TimelineLite {
        if(!this._isAttentionPlaying) {
            return new TimelineLite();
        }

        this._isAttentionPlaying = false;
        this._removeAttentionTimeline = new TimelineLite();
        this._removeAttentionTimeline.add(() => {
            this._fireLoopTimeline.pause();
            this._fireLoopTimeline.kill();
            this._flickingTween.pause();
            this._flickingTween.kill();
        });
        this._removeAttentionTimeline.add([
            this._fireOverlay.getAnimation([41]),
            TweenLite.to(this._flickingOverlay, 0.1, {alpha : 0})
        ]);
        this._removeAttentionTimeline.add(() => this.removeChild(this._attentionOverlayContainer));

        return this._removeAttentionTimeline;
    }

    public playFreespinScatterAttentionOverlay():Timeline {
        if(this._isFsFirePlaying) {
            return new TimelineLite();
        }

        if(!this._freespinOverlayContainer) {
            this._fsFireOverlay = new TimelineSprite(GameResources.getTextures("S_fire"));
            this._fsFireOverlay.blendMode = PIXI.BLEND_MODES.ADD;
            this._fsFireOverlay.position.set(-GameConfig.instance.SYMBOL_WIDTH * 0.5 - 138, -GameConfig.instance.SYMBOL_HEIGHT * 0.5 - 300);
            this._fsFireOverlay.scale.set(2, 4);

            this._freespinOverlayContainer = new PIXI.Container();
            this._freespinOverlayContainer.addChild(this._fsFireOverlay);
        }

        const timeline:TimelineLite = new TimelineLite();
        timeline.add(() => {
            this.loopFsFire();
            this.addChild(this._freespinOverlayContainer);
            this.playFireSound();
        });
        this._isFsFirePlaying = true;

        return timeline;
    }

    private loopFsFire():void {
        this._fsFireLoopTimeline = this._fsFireOverlay.getAnimation([17, 40], undefined, () => this.loopFsFire());
    }

    public removeFreespinScatterAttentionOverlay():TimelineLite {
        if(!this._isFsFirePlaying) {
            return new TimelineLite();
        }

        this._isFsFirePlaying = false;
        this._removeFsAttentionTimeline = new TimelineLite();
        this._removeFsAttentionTimeline.add(() => {
            this._fsFireLoopTimeline.pause();
            this._fsFireLoopTimeline.kill();
        });
        this._removeFsAttentionTimeline.add([
            this._fsFireOverlay.getAnimation([41]),
            TweenLite.to(this._freespinOverlayContainer.scale, 1, {x : 1, y : 1})
        ]);
        this._removeFsAttentionTimeline.add(() => this.removeChild(this._freespinOverlayContainer));

        return this._removeFsAttentionTimeline;
    }


    protected playFireSound():void {
        SlotGame.sound.playEffect(WildNudgeSoundConfig.instance.FLY_BY_FIRE);
    }

    protected reset(remove:boolean):void {
        super.reset(remove);

        // make sure stop all timelines before removing symbol.
        if(this._fireLoopTimeline && this._fireLoopTimeline.isActive()) {
            this._fireLoopTimeline.pause();
            this._fireLoopTimeline.kill();
        }
        if(this._removeAttentionTimeline && this._removeAttentionTimeline.isActive()) {
            this._removeAttentionTimeline.pause();
            this._removeAttentionTimeline.kill();

        }
        if(this._fsFireLoopTimeline && this._fsFireLoopTimeline.isActive()) {
            this._fsFireLoopTimeline.pause();
            this._fsFireLoopTimeline.kill();
        }
        if(this._removeFsAttentionTimeline && this._removeFsAttentionTimeline.isActive()) {
            this._removeFsAttentionTimeline.pause();
            this._removeFsAttentionTimeline.kill();
        }

        this._isAttentionPlaying = false;
        this._isFsFirePlaying = false;

        if(!remove) {
            // Should remove all extra container, but we don't really need it here since there is no symbol replacing.
        }
    }
}